import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MarketingHeader, MarketingFooter, Primary } from "../components/layout/MarketingShell";
import BlogArt from "../components/blog/BlogArt";
import { postBySlug, POSTS, categoryLabel, formatDate } from "../data/blogPosts";

function Arrow({ back = false }) {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {back ? <path d="M19 12H6M11 18l-6-6 6-6" /> : <path d="M5 12h13M13 6l6 6-6 6" />}
    </svg>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = postBySlug(slug);

  /* The app sets no per-route title, so set one here. It is reset by the next
     page load; a proper site-wide title handler is still outstanding. */
  useEffect(() => {
    if (!post) return;
    const previous = document.title;
    document.title = `${post.title} — Bicarra AI`;
    return () => { document.title = previous; };
  }, [post]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="b2 min-h-screen bg-b2-canvas text-b2-ink font-sans">
      <MarketingHeader />

      <main>
        <article className="max-w-[720px] mx-auto px-6 pt-10 pb-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[14px] font-medium text-b2-inksoft
                       hover:text-b2-ink transition-colors"
          >
            <Arrow back /> Semua artikel
          </Link>

          <p className="mt-8 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-b2-indigo">
            {post.eyebrow || categoryLabel(post.category)}
          </p>

          <h1 className="mt-4 text-[36px] sm:text-[42px] leading-[1.14] font-bold tracking-[-0.035em]">
            {post.title}
          </h1>

          <p className="mt-4 text-[17px] leading-relaxed text-b2-inksoft">{post.excerpt}</p>

          <p className="mt-6 text-[13.5px] text-b2-faint">
            {formatDate(post.date)} &middot; {post.readMinutes} minit bacaan
          </p>
        </article>

        <div className="max-w-[860px] mx-auto px-6 pt-6">
          <BlogArt name={post.art} className="h-[300px] rounded-[16px] overflow-hidden" />
        </div>

        <div className="max-w-[720px] mx-auto px-6 pt-12 pb-6">
          {post.body.map((block, i) => (
            <section key={i} className={i > 0 ? "mt-9" : ""}>
              {block.h2 && (
                <h2 className="text-[23px] leading-[1.28] font-bold tracking-[-0.025em] mb-3">
                  {block.h2}
                </h2>
              )}
              {(block.p || []).map((text, j) => (
                <p key={j} className="mt-3 first:mt-0 text-[16.5px] leading-[1.72] text-b2-inksoft">
                  {text}
                </p>
              ))}
              {block.list && (
                <ul className="mt-4 space-y-2.5">
                  {block.list.map((item, j) => (
                    <li key={j} className="flex gap-3 text-[16.5px] leading-[1.6] text-b2-inksoft">
                      <span className="mt-[0.62em] w-[5px] h-[5px] rounded-full bg-b2-indigo shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* CTA */}
        <section className="max-w-[1120px] mx-auto px-6 pt-8 pb-14">
          <div className="bg-b2-wash rounded-[16px] px-8 sm:px-12 py-11
                          flex flex-col lg:flex-row lg:items-center lg:justify-between gap-7">
            <div>
              <h2 className="text-[26px] leading-[1.2] font-bold tracking-[-0.03em]">
                Daripada membaca, kepada mencuba.
              </h2>
              <p className="mt-2.5 text-[15.5px] text-b2-inksoft">
                Bina pembantu AI untuk perniagaan anda.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-center gap-2.5 shrink-0">
              <Primary to="/register" testid="post-cta-register">Mula sekarang</Primary>
              <p className="text-[13px] text-b2-faint">Tanpa perlu menulis kod.</p>
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-[1120px] mx-auto px-6 pb-16">
            <h2 className="text-[22px] font-bold tracking-[-0.03em]">Baca seterusnya</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((p) => (
                <article key={p.slug}
                         className="bg-white border border-b2-line rounded-[14px] p-6
                                    hover:border-b2-linestrong transition-colors">
                  <p className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-b2-indigo">
                    {p.eyebrow || categoryLabel(p.category)}
                  </p>
                  <h3 className="mt-3 text-[18px] leading-[1.3] font-bold tracking-[-0.02em]">
                    <Link to={`/blog/${p.slug}`} className="hover:text-b2-indigo transition-colors">
                      {p.title}
                    </Link>
                  </h3>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="mt-4 inline-flex items-center gap-2 text-[14.5px] font-semibold
                               text-b2-indigo hover:text-b2-indigohover transition-colors"
                  >
                    Baca artikel <Arrow />
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      <MarketingFooter />
    </div>
  );
}
